import React, { useEffect, useState, useMemo } from 'react';
import { Stack, Title, Text, Button, Group, Divider, Paper, List, Loader, Center, Alert, Table, Select, Pagination, Grid, LoadingOverlay } from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import { highlightVariables } from '../../utils/messageUtils';
import { useCampaign } from '../../context/CampaignContext';
import { formatDateTime } from '../../utils/timeUtils';
import { useSalon } from '../../context/SalonContext';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import EditCampaignForm from './EditCampaignForm';
import { notifications } from '@mantine/notifications';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import moment from 'moment-timezone';

function ViewCampaign() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { businessInfo } = useSalon();
  const { 
    campaignDetails, 
    campaignMessages,
    campaignLoading,
    campaignErrors,
    fetchCampaignDetails,
    updateCampaign,
    deleteCampaign
  } = useCampaign();
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [hasChanged, setHasChanged] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Message list state
  const [messagePage, setMessagePage] = useState(1);
  const [messagePageSize, setMessagePageSize] = useState(10);
  const [messageStatusFilter, setMessageStatusFilter] = useState('');
  const [messageSortField, setMessageSortField] = useState('sent_at');
  const [messageSortDirection, setMessageSortDirection] = useState('desc');

  useEffect(() => {
    if (id) {
      fetchCampaignDetails(id);
    }
  }, [id, fetchCampaignDetails]);

  useEffect(() => {
    if (campaignDetails) {
      setFormData({
        name: campaignDetails.name || '',
        description: campaignDetails.description || ''
      });
    }
  }, [campaignDetails]);

  // Check if campaign is scheduled and in the future
  const isScheduledAndInFuture = useMemo(() => {
    if (!campaignDetails || !campaignDetails.start_date || !businessInfo?.timezone) {
      return false;
    }
    
    const now = moment();
    const startDate = moment.utc(campaignDetails.start_date).tz(businessInfo?.timezone);
    
    return startDate.isAfter(now);
  }, [campaignDetails, businessInfo?.timezone]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (campaignDetails) {
      const hasAnyChange = field === 'name' 
        ? campaignDetails.name !== value 
        : campaignDetails.description !== value;
      setHasChanged(hasAnyChange);
    } else {
      setHasChanged(true);
    }

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Campaign name is required';
    }
    if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    if (formData.description.trim().length > 256) {
      newErrors.description = 'Description must be at most 256 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!hasChanged) {
      setIsEditing(false);
      return;
    }

    if (validateForm()) {
      const response = await updateCampaign(
        campaignDetails.uuid,
        formData.name,
        formData.description
      );
      
      if (response) {
        notifications.show({
          title: 'Success!',
          message: 'Campaign details updated successfully',
          color: 'green',
        });
        setIsEditing(false);
      } else {
        notifications.show({
          title: 'Error',
          message: campaignErrors.update || 'Failed to update campaign details',
          color: 'red',
        });
      }
    }
  };

  const handleDelete = async () => {
    const response = await deleteCampaign(campaignDetails.uuid);
    
    if (response) {
      notifications.show({
        title: 'Success!',
        message: 'Campaign deleted successfully',
        color: 'green',
      });
      navigate('/admin/marketing');
    } else {
      notifications.show({
        title: 'Error',
        message: campaignErrors.delete || 'Failed to delete campaign',
        color: 'red',
      });
    }
  };

  // Message filtering and sorting
  const filteredMessages = useMemo(() => {
    let result = [...campaignMessages];
    
    if (messageStatusFilter) {
      result = result.filter(msg => msg.status === messageStatusFilter);
    }
    
    result.sort((a, b) => {
      const aValue = a[messageSortField];
      const bValue = b[messageSortField];
      const direction = messageSortDirection === 'asc' ? 1 : -1;
      
      if (aValue < bValue) return -1 * direction;
      if (aValue > bValue) return 1 * direction;
      return 0;
    });
    
    return result;
  }, [campaignMessages, messageStatusFilter, messageSortField, messageSortDirection]);

  // Message pagination
  const totalMessagePages = Math.ceil(filteredMessages.length / messagePageSize);
  const pagedMessages = filteredMessages.slice(
    (messagePage - 1) * messagePageSize,
    messagePage * messagePageSize
  );

  if (campaignLoading.details) {
    return (
      <Center h={300}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (campaignErrors.details) {
    return (
      <Stack p="md">
        <Button variant="subtle" onClick={() => navigate('/admin/marketing')}>
          &larr; Back
        </Button>
        <Alert color="red" title="Error">
          {campaignErrors.details}
        </Alert>
      </Stack>
    );
  }

  if (!campaignDetails) {
    return null;
  }

  return (
    <Stack gap="md" p="md">
      <LoadingOverlay visible={campaignLoading.delete} />
      <Group justify="space-between">
        <Button variant="subtle" onClick={() => navigate('/admin/marketing')} style={{ alignSelf: 'flex-start' }}>&larr; Back</Button>
        {isEditing ? (
          <Group>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={campaignLoading.update}>
              Save Changes
            </Button>
          </Group>
        ) : (
          <Group>
            {isScheduledAndInFuture && (
              <Button 
                color="red" 
                variant="subtle"
                onClick={() => setShowDeleteModal(true)}
                loading={campaignLoading.delete}
              >
                Delete
              </Button>
            )}
            <Button onClick={() => setIsEditing(true)}>Edit</Button>
          </Group>
        )}
      </Group>
      <Title order={3}>Campaign Details</Title>
      <Grid w="100%">
        <Grid.Col span={6}>
          <Stack gap="xs">
            {isEditing ? (
              <EditCampaignForm 
                formData={formData}
                errors={errors}
                onInputChange={handleInputChange}
              />
            ) : (
            <>
              <Group>
                <Text fw={600}>Campaign Name:</Text>
                <Text>{campaignDetails.name}</Text>
              </Group>
              <Group>
                <Text fw={600}>Description:</Text>
                <Text>{campaignDetails.description}</Text>
              </Group>
            </>
            )}
            <Group>
              <Text fw={600}>Created Date:</Text>
              <Text>{formatDateTime(campaignDetails.created_dt, businessInfo?.timezone, 'MMM D, YYYY h:mm A')}</Text>
            </Group>
            <Group>
              <Text fw={600}>Start Date:</Text>
              <Text>{formatDateTime(campaignDetails.start_date, businessInfo?.timezone, 'MMM D, YYYY h:mm A')}</Text>
            </Group>
            {campaignDetails.end_date && (
                <Group>
                  <Text fw={600}>End Date:</Text>
                  <Text>{formatDateTime(campaignDetails.end_date, businessInfo?.timezone, 'MMM D, YYYY h:mm A')}</Text>
                </Group>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={6}>
          <Stack gap="xs">
            <Group>
              <Text fw={600}>Message Type:</Text>
              <Text>{campaignDetails.message_type}</Text>
            </Group>
            <Group>
              <Text fw={600}>Total Sent:</Text>
              <Text>{campaignDetails.total_sent}</Text>
            </Group>
            <Group>
              <Text fw={600}>Click Rate:</Text>
              <Text>{(campaignDetails.click_rate * 100).toFixed(1)}%</Text>
            </Group>
            <Group>
              <Text fw={600}>Conversion Rate:</Text>
              <Text>{(campaignDetails.conversion_rate * 100).toFixed(1)}%</Text>
            </Group>
          </Stack>
        </Grid.Col>
      </Grid>
        
      <Divider my="sm" />
      
      <Title order={4}>Messages</Title>
      <Group>
        <Select
          label="Status Filter"
          value={messageStatusFilter}
          onChange={setMessageStatusFilter}
          data={[
            { value: '', label: 'All Statuses' },
            ...campaignDetails.messages.reduce((acc, message) => {
              if (!acc.some(item => item.value === message.status)) {
                acc.push({ 
                  value: message.status,
                  label: message.status.charAt(0) + message.status.slice(1).toLowerCase()
                });
              }
              return acc;
            }, [])
          ]}
        />
        <Select
          label="Sort By"
          value={messageSortField}
          onChange={setMessageSortField}
          data={[
            { value: 'client_name', label: 'Client Name' },
            { value: 'sent_at', label: 'Sent Date' },
            { value: 'status', label: 'Status' }
          ]}
        />
        <Select
          label="Sort Direction"
          value={messageSortDirection}
          onChange={setMessageSortDirection}
          data={[
            { value: 'asc', label: 'Ascending' },
            { value: 'desc', label: 'Descending' }
          ]}
        />
      </Group>
      
      {campaignLoading.messages ? (
        <Center h={200}>
          <Loader size="md" />
        </Center>
      ) : (
        <>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Client</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Sent At</Table.Th>
                <Table.Th>Failure Reason</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pagedMessages.map((message) => (
                <Table.Tr key={message.uuid}>
                  <Table.Td>{message.client_name}</Table.Td>
                  <Table.Td>{formatPhoneNumber(`${message.area_code}${message.phone_number}`)}</Table.Td>
                  <Table.Td>{message.status}</Table.Td>
                  <Table.Td>{message.sent_at ? formatDateTime(message.sent_at, businessInfo?.timezone, 'MM D, YYYY h:mm A') : '-'}</Table.Td>
                  <Table.Td>{message.failure_reason || '-'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          
          <Group justify="space-between" mt="md">
            <Select
              value={messagePageSize.toString()}
              onChange={(v) => {
                setMessagePageSize(Number(v));
                setMessagePage(1);
              }}
              data={[10, 20, 50].map(n => ({ value: n.toString(), label: n.toString() }))}
              style={{ width: 100 }}
            />
            <Pagination
              page={messagePage}
              onChange={setMessagePage}
              total={totalMessagePages}
            />
          </Group>
        </>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign? This action cannot be undone."
      />
    </Stack>
  );
}

export default ViewCampaign; 